import { VStack, Box, Button, MenuList, Text } from '@cad-arc';
import { observer } from 'mobx-react';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { NEW_CONVERSATION_ID } from '../constants';
import { closeAndClearActiveFilterTabMenu } from '../helpers/conversations-filter-helpers';
import { useMessagingStore } from '../hooks/use-messaging-store';
import { useNewConversationCancellationModal } from '../hooks/use-new-conversation-cancellation-modal';
import { useSelectFirstConversation } from '../hooks/use-select-first-conversation';
import { ConversationKey } from '../models';
import { ConversationItem } from './conversation-item';
import styles from './conversations-subpanel.module.css';

export const Conversations = observer(
    ({ setShowAllConversations }: { setShowAllConversations?: (value: boolean) => void }) => {
        const {
            conversationListKeys,
            isBookmarksView,
            isCreatingNewConversation,
            participantFiltersInfo,
            selectedConversationFilter,
            selectedConversationKey,
            setActiveFilterTabMenu,
            setIsBookmarksView,
            setSelectedConversationKey,
            bookmarkedConversationItems,
            conversationItems,
        } = useMessagingStore();

        const { filterType } = selectedConversationFilter;

        const { isFirstResponder } = useApplicationType();

        const {
            setIsOpen,
            updateConfirmCancellationStoredData,
        } = useNewConversationCancellationModal();

        useSelectFirstConversation(conversationListKeys, setSelectedConversationKey);

        const newConversationKey = new ConversationKey(NEW_CONVERSATION_ID, undefined);

        const conversations = isBookmarksView ? bookmarkedConversationItems : conversationItems;

        if (participantFiltersInfo === undefined) {
            // data hasn't loaded yet
            return <></>;
        }

        return (
            <VStack
                className={styles['conversations-subpanel']}
                data-testid="conversations-subpanel"
            >
                <Box
                    width={isFirstResponder ? '100%' : 'auto'}
                    padding="var(--arc-space-2)"
                    paddingLeft="var(--arc-space-3)"
                >
                    {!isBookmarksView && (
                        <Button
                            style={{ width: 'inherit', paddingLeft: '8px' }}
                            data-testid="new-conversation-button"
                            variant={isFirstResponder ? 'outline' : 'ghost'}
                            leadingVisual="Add"
                            onClick={() => {
                                if (isFirstResponder && setShowAllConversations) {
                                    setShowAllConversations(false);
                                }
                                closeAndClearActiveFilterTabMenu({
                                    filterType: newConversationKey.participantIdentity?.entityType,
                                    setActiveFilterTabMenu,
                                });
                                updateConfirmCancellationStoredData?.(undefined);
                                setSelectedConversationKey(newConversationKey);
                            }}
                        >
                            New Conversation
                        </Button>
                    )}
                    {isBookmarksView && (
                        <>
                            <Button
                                style={{ paddingLeft: '8px' }}
                                data-testid="back-to-conversations-button"
                                variant="ghost"
                                leadingVisual="ArrowLeft"
                                onClick={() => {
                                    setIsBookmarksView(false);
                                }}
                            >
                                {isFirstResponder
                                    ? 'View All Conversations'
                                    : 'Back to conversations'}
                            </Button>
                            <Box marginTop="var(--arc-space-3)">
                                <Text data-testid="bookmarks-header" variant="headingSm">
                                    Bookmarks
                                </Text>
                            </Box>
                        </>
                    )}
                </Box>
                <Box w="100%" m="0 !important" overflowY="auto">
                    <MenuList
                        className={styles['conversation-list']}
                        data-testid="conversation-list"
                    >
                        {conversations.map(
                            ({
                                conversationKey,
                                allOtherMembersLabel,
                                allOtherMembersTooltip,
                                senderParticipantLabel,
                                lastMessageDateTimeDisplay,
                                unreadCount,
                                hasUrgentUnread,
                            }) => {
                                const isSelected = conversationKey.equals(selectedConversationKey);
                                return (
                                    <Box
                                        key={conversationKey.toString()}
                                        padding="var(--arc-space-half) 0"
                                    >
                                        <ConversationItem
                                            data-testconversationkey={conversationKey.toString()}
                                            allOtherMembersLabel={allOtherMembersLabel}
                                            allOtherMembersTooltip={allOtherMembersTooltip}
                                            senderParticipantLabel={senderParticipantLabel}
                                            lastMessageDateTimeDisplay={lastMessageDateTimeDisplay}
                                            isSelected={isSelected}
                                            unreadCount={unreadCount}
                                            hasUrgentUnread={hasUrgentUnread}
                                            onConversationItemClick={() => {
                                                if (isFirstResponder && setShowAllConversations) {
                                                    setShowAllConversations(false);
                                                }
                                                closeAndClearActiveFilterTabMenu({
                                                    filterType:
                                                        conversationKey.participantIdentity
                                                            ?.entityType,
                                                    setActiveFilterTabMenu,
                                                });
                                                if (isCreatingNewConversation) {
                                                    updateConfirmCancellationStoredData?.(
                                                        conversationKey
                                                    );
                                                    setIsOpen?.(true);
                                                } else {
                                                    setSelectedConversationKey(conversationKey);
                                                }
                                            }}
                                            showUnread={
                                                !isBookmarksView && !(filterType === 'INACTIVE')
                                            }
                                        />
                                    </Box>
                                );
                            }
                        )}
                    </MenuList>
                </Box>
            </VStack>
        );
    }
);
